import * as React from "react";

/**
 * Viewport threshold below which we render the dedicated mobile tree. Phones
 * only — tablets (>= 768px) keep the already-responsive desktop layout. Matches
 * the 767px phone breakpoint already used in components.css so we don't invent
 * a third breakpoint vocabulary.
 */
export const MOBILE_QUERY = "(max-width: 767px)";

/**
 * useLayoutEffect warns when run during SSR (renderToString). The mobile tree
 * is never rendered on the server — marketing routes use a CSS-only path (see
 * mobile-field.css) — but guard anyway so this hook is safe to import anywhere.
 */
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

/**
 * Single source of truth for "should we render the mobile view?".
 *
 * Hydration contract: returns `false` during SSR/prerender AND on the first
 * client render, so it always agrees with the prerendered desktop markup
 * (scripts/prerender.mjs renders the desktop `.v-field` tree; useEffect/state
 * updates never run during renderToString). It flips to the real value in a
 * layout effect, before the browser paints, so on client-only routes there is
 * no visible flash.
 *
 * Width-based on purpose: layout viability is a width question. Touch is an
 * INPUT concern, handled separately by the games via `pointer: coarse`
 * (see PlayShell.tsx) — keep the two orthogonal.
 */
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = React.useState(false);

  useIsomorphicLayoutEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return;
    }
    const mq = window.matchMedia(MOBILE_QUERY);
    const update = () => setIsMobile(mq.matches);
    update(); // correct the SSR-safe default before first paint
    // 'change' fires on resize across the threshold AND on orientation change.
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return isMobile;
}
